import type { ActivityLog, ActivityLogTab } from './types';
import { isAnonymousMergeEvent } from './activity-log-types';

/**
 * What the feed records about one row, wherever the row is shown.
 *
 * Expanding a row is the row's own event, so it travels with it: the page
 * this replaces fires it from inside the row
 * (`ui/app/components/activity-log-row/index.gts:100-110`), which is why
 * every surface rendering the row reports it rather than only the feed.
 */

/** The `Activity Tab` property value shared by all three page events. */
export function activityTabName(tab: ActivityLogTab): 'Identified' | 'Anonymous' {
  return tab === 'anonymous' ? 'Anonymous' : 'Identified';
}

/**
 * Wire value for a profile-merge **source**. Note the space: this is
 * `ActivityLogSourceType.ProfileMerge` from Ember (`ui/app/utils/activity-log-source-types.ts`),
 * not the similarly-named `LOG_TYPE.ProfileMerge` log type (`'profile_merge'`, underscore).
 */
const PROFILE_MERGE_SOURCE = 'profile merge';

/**
 * The log records an activity that came from merging two people — i.e. one of its *sources* is a
 * profile merge, which is not the same population as logs whose own type is `LOG_TYPE.ProfileMerge`
 * (those are the merge itself). Ported from Ember's `LogModel.isPeopleMergeEvent`
 * (`ui/app/models/log.ts`), which is the definition behind the historical `Merge Activity Type`
 * series — so switching this to the log type would silently break continuity with it.
 */
function isPeopleMergeEvent(log: ActivityLog): boolean {
  return (log.sources ?? []).some((source) => source.type === PROFILE_MERGE_SOURCE);
}

type SpecificActivityViewedProps = {
  'Activity Tab'?: ReturnType<typeof activityTabName>;
  'Merged Anonymous Event': boolean;
  'Merge Activity Type': boolean;
};

/**
 * Properties for `Log: Activity Log Specific Activity Viewed` (a row toggle).
 *
 * Ember's transform reads `Activity Tab` from the logs route's `:tab` param (`ui/app/utils/analytics/
 * transforms/logs.ts`), which only the workspace activity page has. Every other surface that shows
 * the row leaves the property out, so pass `tab` from that page alone.
 */
export function specificActivityViewedProps(log: ActivityLog, tab?: ActivityLogTab) {
  const props: SpecificActivityViewedProps = {
    'Merged Anonymous Event': isAnonymousMergeEvent(log),
    'Merge Activity Type': isPeopleMergeEvent(log),
  };
  if (tab != null) props['Activity Tab'] = activityTabName(tab);
  return props;
}
